import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect } from 'react';
import OrderManagerStore from '../stores/OrderOverviewStore';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import OrderDialogStore from '../stores/OrderDialogStore';
import OrderEditDialogStore from '../stores/OrderEditDialogStore';
import { formatDate } from '../../../../utils/helper';
import { Dropdown } from 'primereact/dropdown';
import { statusOptions, delieveredOptions } from '../../../../core/constants';
import { MatenFilter, MatenTable, MatenTableAction, MatenTableColumn } from '../../../../shared';

const OrderStatus = ({ status }: any) => {
    let useStatusText: string = 'NEU';
    let useStatusSeverity: any = 'success';

    if (status === 'COMPLETED') {
        useStatusText = 'FERTIG';
        useStatusSeverity = 'info';
    } else if (status === 'ARCHIVED') {
        useStatusText = 'ARCHIVIERT';
        useStatusSeverity = 'warning';
    }
    return (
        <Tag
            value={useStatusText}
            severity={useStatusSeverity}
        ></Tag>
    );
};

const OrderOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(OrderManagerStore);

        const refreshOnTabActive = () => {
            if (document.visibilityState === 'visible') {
                sdk.currentStore.refresh();
            }
        };

        document.addEventListener('visibilitychange', refreshOnTabActive);

        return () => {
            document.removeEventListener('visibilitychange', refreshOnTabActive);
        };
    }, [sdk]);

    // Define columns for orders
    const columns: MatenTableColumn[] = [
        {
            field: 'status',
            header: 'Status',
            sortable: false,
            style: { textAlign: 'center' },
            body: (rowData: any) => <OrderStatus status={rowData.status} />,
        },
        {
            field: 'id',
            header: 'Bestellnummer',
            sortable: true,
            body: (rowData: any) => `#${`${rowData.id}`.padStart(5, '0')}`,
        },
        {
            field: 'object_name',
            header: 'Objekt',
            sortable: false,
        },
        {
            field: 'employee',
            header: 'Reinigungskraft',
            sortable: false,
            body: (rowData: any) => {
                // Handle both string and object formats
                if (typeof rowData.employee === 'string') {
                    return rowData.employee;
                } else if (rowData.employee && typeof rowData.employee === 'object') {
                    return (
                        `${rowData.employee.firstname || ''} ${rowData.employee.lastname || ''}`.trim() ||
                        '-'
                    );
                } else {
                    return '-';
                }
            },
        },
        {
            field: 'created',
            header: 'Erstellt',
            sortable: true,
            body: (rowData: any) => formatDate(rowData.created_utc),
        },
        {
            field: 'delievered',
            header: 'Geliefert',
            sortable: true,
            body: (rowData: any) =>
                rowData.delievered_utc ? formatDate(rowData.delievered_utc, true) : '-',
        },
    ];

    // Define actions for orders
    const actions: MatenTableAction[] = [
        {
            icon: 'pi pi-pencil',
            tooltip: 'Bestellung bearbeiten',
            tooltipPosition: 'left',
            onClick: (rowData: any) => {
                sdk.openDialog(OrderDialogStore, { order_id: rowData.id });
            },
            testId: 'edit-order-btn',
        },
        {
            icon: 'pi pi-trash',
            tooltip: 'Bestellung löschen',
            tooltipPosition: 'left',
            onClick: async (rowData: any) => {
                const result = await sdk.getDialogDelete({
                    title: 'Bestellung löschen',
                    message: `Möchten Sie die Bestellung '${`#${`${rowData.id}`.padStart(5, '0')}`}' wirklich löschen? Gelöschte Bestellungen können nicht wiederhergestellt werden.`,
                    searchWord: 'löschen',
                    onConfirm: async () => sdk.currentStore.deleteOrder(rowData.id),
                });

                if (result.confirmed) {
                    sdk.showSuccessToast('Bestellung erfolgreich gelöscht');
                } else if (result.error) {
                    sdk.showErrorToast(result.error);
                }
            },
            testId: 'delete-order-btn',
            className: 'p-button-danger',
        },
    ];

    // Additional header content
    const headerContent = (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
                label={'Neu'}
                icon="pi pi-plus"
                aria-label="Filter"
                onClick={() => sdk.openDialog(OrderEditDialogStore, {})}
                style={{ marginRight: '0.5rem' }}
                data-testid="add-order-btn"
            />
            <Button
                icon="pi pi-sync"
                aria-label="Aktualisieren"
                onClick={() => sdk.currentStore.refresh()}
                disabled={!!sdk.currentStore?.refreshTimer}
                data-testid="refresh-orders-btn"
            />
        </div>
    );

    const tableFilter: MatenFilter[] = [
        {
            title: 'Status',
            property: 'filterStatus',
            removeFilter: (store: any) => store.applyFilterStatus(null),
            chipContent: (filter: any) =>
                `Status: ${statusOptions.find((status: any) => status.value === filter)?.label}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterStatus || null}
                    options={statusOptions}
                    onChange={(e: any) => store.applyFilterStatus(e.value)}
                    placeholder="Status auswählen"
                    optionLabel="label"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="status-filter-dropdown"
                />
            ),
        },
        {
            property: 'filterDelievered',
            removeFilter: (store: any) => store.applyFilterDelivered(null),
            chipContent: (filter: any) => `Geliefert: ${filter ? 'Ja' : 'Nein'}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterDelievered ?? null}
                    options={delieveredOptions}
                    onChange={(e: any) => {
                        if (e.value === 'null') {
                            store.applyFilterDelivered(null);
                        } else {
                            store.applyFilterDelivered(e.value);
                        }
                    }}
                    placeholder="Geliefert Status"
                    optionLabel="label"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="delivered-filter-dropdown"
                />
            ),
        },
        {
            title: 'Objektleiter auswählen',
            property: 'filterManager',
            removeFilter: (store: any) => store.applyFilterManager(null),
            chipContent: (filter: any) => `Objektleiter: ${filter?.name || ''}`,
            filterComponent: (store: any) => (
                <Dropdown
                    value={store?.filterManager || null}
                    options={store?.available_managers || []}
                    onChange={(e: any) => store.applyFilterManager(e.value)}
                    onShow={() => store?.fetchAvailableManagers()} // Lazy load managers when dropdown is focused
                    placeholder="Objektleiter auswählen"
                    optionLabel="name"
                    loading={false}
                    style={{ width: '100%' }}
                    data-testid="manager-filter-dropdown"
                />
            ),
        },
    ];

    return (
        <MatenTable
            data={sdk.currentStore?.list}
            store={sdk.currentStore}
            columns={columns}
            actions={actions}
            filter={tableFilter}
            search={{
                placeholder: 'Bestellungen durchsuchen',
            }}
            emptyMessage="Keine Bestellungen gefunden"
            entity={{ singular: 'Bestellung', plural: 'Bestellungen' }}
            testId={'orders-overview'}
            toolbarContent={headerContent}
            pagination={{ rows: 30 }}
        />
    );
});

export default OrderOverview;
